import { Error404 } from './pages/errors';
import Home from './pages/home';
import BulkQRPage from './pages/bulk-qr';
import Login from './pages/login';
import MpinPage from './pages/mpin';
import SsoCallbackPage from './pages/sso-callback/SsoCallbackPage';
import Settings from './pages/settings/Settings';
import Staff from './pages/staff';
import StaffDetail from './pages/staff-details/StaffDetail';
import Attendance from './pages/attendance';
import { RouteLayouts } from './utils/constants';
import Report from './pages/report';
import MapView from './pages/map-view';
import Contacts from './pages/contacts';
import PayOnline from './pages/pay-online';
import MapDetails from './pages/map-view/MapDetails';
import MapDetailsFiltered from './pages/map-view/MapDetailsFiltered';
import Terms from './pages/terms';
import Privacy from './pages/privacy';
import { Vivo, Oppo, Realme } from './pages/extras';
import Hire from './pages/hire';
import AgencyHire from './pages/agency-hire';
import SingleMapView from './pages/map-view/MapDetailsV2';
import RiderMapView from './pages/map-view/MapDetailsV3';
import ControlCenterPage from './pages/control-center';
import QCLeadsPage from './pages/qc-leads';
import RiderOnboardingPage from './pages/rider-onboarding';
import RiderManagementPage from './pages/rider-management';
import HubManagementPage from './pages/hub-management';
import StoreHubsPage from './pages/store-hubs';
import VendorManagementPage from './pages/vendor-management';
import UserManagementPage from './pages/user-management';
import ApprovalWorkflowPage from './pages/approval-workflow';
import ClusterManagementPage from './pages/cluster-management';
import SingleTeamLeadPage from './pages/cluster-management/team-lead';
import SingleTeamLeadRiderPage from './pages/cluster-management/rider';
import EVPage from './pages/ev';
import SingleEV from './pages/ev/SingleEV';
import EvReportPage from './pages/ev/EvReport';
import TeamLeadMapView from './pages/map-view/TeamLeadMapView';
import Remittance from './pages/remittance';
import ReferralReport from './pages/agency-hire/ReferralReport';
import AmzRerun from './pages/amz-rerun';
import BgvTrackingPage from './pages/bgv-tracking';

const routes = [
    // MPIN gate — full-screen second factor between OTP login and the app.
    // BlankLayout: no navbar, no token check (the page guards itself).
    {
        path: '/mpin',
        component: MpinPage,
        name: 'PIN',
        layout: RouteLayouts.BlankLayout,
    },
    {
        path: '/login',
        component: Login,
        name: 'Login',
        layout: RouteLayouts.BlankLayout,
    },
    {
        path: '/terms',
        component: Terms,
        name: 'Terms',
        layout: RouteLayouts.BlankLayout,
    },
    {
        path: '/privacy',
        component: Privacy,
        name: 'Privacy',
        layout: RouteLayouts.BlankLayout,
    },
    {
        path: '/404',
        component: Error404,
        name: 'Error 404',
        layout: RouteLayouts.BlankLayout,
    },
    {
        path: '/sso-callback',
        component: SsoCallbackPage,
        name: 'SSO Callback',
        layout: RouteLayouts.BlankLayout,
    },
    {
        path: '/settings',
        component: Settings,
        keepInNav: false,
        name: 'Settings',
        layout: RouteLayouts.MainLayout,
    },
    {
        path: '/staff/details/:id?',
        component: StaffDetail,
        keepInNav: false,
        name: 'Staff Details',
        layout: RouteLayouts.MainLayout,
    },
    {
        path: '/staff',
        component: Staff,
        keepInNav: true,
        name: 'Staff',
        order: 2,
        layout: RouteLayouts.MainLayout,
    },
    {
        path: '/ev-sp/report',
        component: EvReportPage,
        keepInNav: false,
        name: 'EV Report',
        layout: RouteLayouts.MainLayout,
        containerStyles: {
            marginBottom: '4px',
        },
    },
    {
        path: '/ev-sp/details/:id?',
        component: SingleEV,
        keepInNav: false,
        name: 'EV Details',
        layout: RouteLayouts.MainLayout,
        containerStyles: {
            marginBottom: '4px',
        },
    },
    {
        path: '/ev-sp',
        component: EVPage,
        keepInNav: true,
        name: 'EV SP',
        routeName: 'EV',
        order: 1,
        layout: RouteLayouts.MainLayout,
        containerStyles: {
            marginBottom: '4px',
        },
    },
    {
        path: '/ev-cm',
        component: EVPage,
        keepInNav: true,
        name: 'EV CM',
        routeName: 'EV',
        order: 1,
        layout: RouteLayouts.MainLayout,
        containerStyles: {
            marginBottom: '4px',
        },
    },
    {
        path: '/qc-leads',
        component: QCLeadsPage,
        keepInNav: true,
        name: 'QC Leads',
        order: 2,
        layout: RouteLayouts.MainLayout,
        containerStyles: {
            marginBottom: '4px',
        },
    },
    {
        path: '/rider-management',
        component: RiderManagementPage,
        keepInNav: true,
        name: 'Rider Management',
        order: 2,
        layout: RouteLayouts.MainLayout,
        containerStyles: {
            marginBottom: '4px',
        },
    },
    {
        // MUST stay ahead of /ops-management. AppRoute renders these inside a <Switch>, which
        // picks a child by its `path` prop alone — the exact={true} lives on MainLayout's inner
        // Route, where Switch cannot see it. Ordered the other way, /ops-management wins the
        // match and then its own exact Route declines to render, leaving a blank page.
        // The Navbar is unaffected: it resolves the active tab with pathname.includes(route.path)
        // and no real pathname contains the literal ":storeId", so it still finds the parent.
        path: '/ops-management/store/:storeId',
        component: StoreHubsPage,
        keepInNav: false,
        name: 'Store Hubs',
        layout: RouteLayouts.MainLayout,
    },
    {
        path: '/ops-management',
        component: HubManagementPage,
        keepInNav: true,
        name: 'OPS Management',
        order: 2,
        layout: RouteLayouts.MainLayout,
        containerStyles: {
            marginBottom: '4px',
        },
    },
    {
        path: '/approval-workflow',
        component: ApprovalWorkflowPage,
        keepInNav: true,
        name: 'Approval Workflow',
        order: 2,
        layout: RouteLayouts.MainLayout,
        containerStyles: {
            marginBottom: '4px',
        },
    },
    {
        path: '/user-management',
        component: UserManagementPage,
        keepInNav: true,
        // The Navbar derives the permission key from this name: "User Management" ->
        // user_management, which must match the route path in kebab-case or getActiveInitRoute()
        // redirects to a nonexistent route.
        name: 'User Management',
        order: 2,
        layout: RouteLayouts.MainLayout,
        containerStyles: {
            marginBottom: '4px',
        },
    },
    {
        path: '/vendor-management',
        component: VendorManagementPage,
        keepInNav: true,
        name: 'Vendor Management',
        order: 2,
        layout: RouteLayouts.MainLayout,
        containerStyles: {
            marginBottom: '4px',
        },
    },
    {
        path: '/cluster-management/details/:id?/rider/:riderId?',
        component: SingleTeamLeadRiderPage,
        name: 'Cluster Management',
        keepInNav: false,
        routeName: 'TLs',
        order: 1,
        layout: RouteLayouts.MainLayout,
        containerStyles: {
            marginBottom: '4px',
        },
    },
    {
        path: '/cluster-management/details/:id?',
        component: SingleTeamLeadPage,
        name: 'Cluster Management',
        keepInNav: false,
        routeName: 'TLs',
        order: 1,
        layout: RouteLayouts.MainLayout,
        containerStyles: {
            marginBottom: '4px',
        },
    },
    {
        path: '/cluster-management',
        component: ClusterManagementPage,
        keepInNav: true,
        name: 'Cluster Management',
        routeName: 'TLs',
        order: 1,
        layout: RouteLayouts.MainLayout,
        containerStyles: {
            marginBottom: '4px',
        },
    },
    {
        path: '/attendance',
        component: Attendance,
        keepInNav: true,
        name: 'Attendance',
        order: 3,
        layout: RouteLayouts.MainLayout,
    },
    {
        path: '/hire',
        component: Hire,
        keepInNav: true,
        name: 'Hire',
        order: 4,
        layout: RouteLayouts.MainLayout,
    },
    {
        path: '/agency-hire',
        component: AgencyHire,
        keepInNav: true,
        name: 'Agency',
        order: 5,
        layout: RouteLayouts.MainLayout,
    },

    {
        path: '/referral-report',
        component: ReferralReport,
        keepInNav: true,
        name: 'Referral Report',
        order: 6,
        layout: RouteLayouts.MainLayout,
    },
    {
        path: '/map-view/detail/:id?',
        component: RiderMapView,
        keepInNav: false,
        name: 'Map Detail',
        order: 5,
        layout: RouteLayouts.MainLayout,
        containerStyles: {
            marginBottom: '4px',
        },
    },
    {
        path: '/map-view/tl-detail/:id?',
        component: TeamLeadMapView,
        keepInNav: false,
        name: 'Map Detail',
        order: 5,
        layout: RouteLayouts.MainLayout,
        containerStyles: {
            marginBottom: '4px',
        },
    },
    {
        path: '/map-view/detail-filtered/:id?',
        component: MapDetailsFiltered,
        keepInNav: false,
        name: 'Map Detail',
        order: 5,
        layout: RouteLayouts.MainLayout,
    },
    {
        path: '/map-view',
        component: MapView,
        keepInNav: true,
        name: 'Map View',
        order: 5,
        layout: RouteLayouts.MainLayout,
    },
    {
        path: '/report',
        component: Report,
        keepInNav: true,
        name: 'Report',
        order: 7,
        layout: RouteLayouts.MainLayout,
    },
    {
        path: '/pay-online',
        component: PayOnline,
        keepInNav: true,
        name: 'Pay Online',
        order: 8,
        layout: RouteLayouts.MainLayout,
    },
    {
        path: '/contacts',
        component: Contacts,
        keepInNav: true,
        name: 'Contacts',
        order: 9,
        layout: RouteLayouts.MainLayout,
    },
    {
        path: '/remittance',
        component: Remittance,
        keepInNav: true,
        name: 'Remittance',
        order: 10,
        layout: RouteLayouts.MainLayout,
    },
    {
        path: '/amz-rerun',
        component: AmzRerun,
        keepInNav: true,
        name: 'AMZ Rerun',
        order: 11,
        layout: RouteLayouts.MainLayout,
    },
    {
        path: '/bgv-tracking',
        component: BgvTrackingPage,
        keepInNav: true,
        name: 'BGV Tracking',
        order: 12,
        layout: RouteLayouts.MainLayout,
    },
    {
        path: '/control-center',
        component: ControlCenterPage,
        keepInNav: true,
        name: 'Control Center',
        order: 8,
        layout: RouteLayouts.MainLayout,
        containerStyles: {
            marginBottom: '4px',
        },
    },
    {
        path: '/home',
        component: Home,
        keepInNav: false,
        name: 'Home',
        order: 1,
        layout: RouteLayouts.MainLayout,
    },
    {
        path: '/bulk-qr',
        component: BulkQRPage,
        name: 'Bulk QR',
        layout: RouteLayouts.BlankLayout,
    },
    {
        path: '/extras/Vivo',
        component: Vivo,
        name: 'Vivo',
        layout: RouteLayouts.BlankLayout,
    },
    {
        path: '/extras/oppo',
        component: Oppo,
        name: 'Oppo',
        layout: RouteLayouts.BlankLayout,
    },
    {
        path: '/extras/realme',
        component: Realme,
        name: 'Realme',
        layout: RouteLayouts.BlankLayout,
    },
];

export default routes;
